import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { ListingCard } from '../../../components';
// Not '../../../components' (src/components/Heading/Heading.js) - that's a separate, generic H1
// with different typography (900 weight, 64px, no letter-spacing). The Console-managed section
// titles elsewhere on this page (bestemmingen, thema's) render through PageBuilder's Field.js,
// which uses THIS H1 (48px/800/-0.5px letter-spacing/colorGrey900) - importing it directly from
// here is what actually reproduces the same look, confirmed against Ron's own font inspection of
// the live "Thema's voor bijzonder overnachten" heading (24 sep 2026: "de H1 is een ander font/
// kleur dan de standaard H1" - the first attempt used the wrong Heading component).
import { H1 } from '../../PageBuilder/Primitives/Heading';
// Same reasoning as the H1 import above: this is the exact primitive Console's own "paragraph"
// field type uses for a lead paragraph under a heading (see PageBuilder/Field.js) - reusing it
// directly reproduces the same grey subtitle look Thema's own description text has (18px,
// inherited colorGrey700), including the 24px gap under the H1 (that spacing comes from a rule
// on the H1 itself for a paragraph directly following it - see Primitives/Heading.module.css).
import { Ingress } from '../../PageBuilder/Primitives/Ingress';

import { getNewestListings } from './NewestListingsSection.duck';
import css from './NewestListingsSection.module.css';

// New (24 sep 2026): a row of the marketplace's newest published listings, shown right below the
// hero on the homepage (Ron - "een rijtje van de 4 nieuwste listings... automatisch update als er
// nieuwe listings worden toegevoegd"). Unlike every other section on this page, this one is not
// managed in Sharetribe Console - Console's page builder has no way to query live listing data,
// only fixed, hand-authored content - so it's rendered directly by LandingPage.js instead (see the
// comment there), always right after whichever section is first (normally the hero).
//
// Renders nothing at all (no heading, no empty box) when there are no published listings yet, or
// while the very first load is still in flight - same "hide rather than show an empty/broken
// section" approach already used for the listing page's reviews section.

// Carousel mechanics below (24 sep 2026, Ron: "uitbreiden naar 6 listings. En naar rechts
// uitlijnen met een swipe button. Zodat het dezelfde layout aanhoudt als het blokje thema's") are
// a deliberate LOCAL COPY of PageBuilder's own SectionCarousel.js/.module.css - same scroll/arrow
// behaviour, same --carouselWidth technique, same per-card sizing formula - rather than importing
// that component/stylesheet directly. This keeps the same "don't touch or depend on PageBuilder
// internals" boundary already established for this whole section (see the comment in
// LandingPage.js): if SectionCarousel ever changes for Console-driven sections, this row is
// unaffected, and vice versa.
//
// numColumns is 3, not 4 (Ron, 24 sep 2026: "de thumbs groter maken zodat ze qua formaat
// overeenkomen met de thumbs van de Thema's" - the previous version used 4, Sharetribe's largest
// built-in preset, as a guess at "biggest available" without actually checking Thema's own card
// size; measuring Thema's real cards against this formula shows Thema's Console section itself
// uses 3 columns, not 4). Per-card width is fixed (not viewport-relative) the same way Thema's
// is, so "een rijtje van 4 tonen, de overige 2 verscholen achter de swipe" - Ron's other ask -
// falls out naturally on a normal desktop window (4 same-size cards + gaps comfortably fit the
// row's own width, which - like Thema's row - now spans the full section instead of being capped
// to the page's usual 1120px content width) rather than being hardcoded; exactly how many show
// before swiping is needed depends on the window's width, same as Thema's own row.
const NUM_COLUMNS = 3;
const SLIDER_CONTAINER_ID = 'newestListingsCarouselContainer';
const SLIDER_ID = 'newestListingsCarouselSlider';
const KEY_CODE_ARROW_LEFT = 37;
const KEY_CODE_ARROW_RIGHT = 39;

export const NewestListingsSectionComponent = props => {
  const { listings } = props;
  const hasListings = listings && listings.length > 0;

  // Same technique as SectionCarousel.js: measure the carousel's own container (not the full
  // window) so --carouselWidth is correct even if a scrollbar or side padding is present, and
  // keep it in sync on resize.
  useEffect(() => {
    const setCarouselWidth = () => {
      if (!hasListings) {
        return;
      }
      const windowWidth = window.innerWidth;
      const elem = window.document.getElementById(SLIDER_CONTAINER_ID);
      if (!elem) {
        return;
      }
      const scrollbarWidth = window.innerWidth - document.body.clientWidth;
      const elementWidth =
        elem.clientWidth >= windowWidth - scrollbarWidth ? windowWidth : elem.clientWidth;
      const carouselWidth = elementWidth - scrollbarWidth;

      elem.style.setProperty('--carouselWidth', `${carouselWidth}px`);
    };
    setCarouselWidth();

    window.addEventListener('resize', setCarouselWidth);
    return () => window.removeEventListener('resize', setCarouselWidth);
  }, [hasListings]);

  if (!hasListings) {
    return null;
  }

  // Same sizes SectionCarousel.js itself uses for its "threeColumns" preset (see NUM_COLUMNS).
  const renderSizes = ['(max-width: 767px) 100vw', '400px'].join(', ');

  const onSlideLeft = e => {
    const slider = window.document.getElementById(SLIDER_ID);
    const slideWidth = NUM_COLUMNS * slider?.firstChild?.clientWidth;
    slider.scrollLeft = slider.scrollLeft - slideWidth;
    // Fix for Safari
    e.target.focus();
  };

  const onSlideRight = e => {
    const slider = window.document.getElementById(SLIDER_ID);
    const slideWidth = NUM_COLUMNS * slider?.firstChild?.clientWidth;
    slider.scrollLeft = slider.scrollLeft + slideWidth;
    // Fix for Safari
    e.target.focus();
  };

  const onKeyDown = e => {
    if (e.keyCode === KEY_CODE_ARROW_LEFT) {
      // Prevent changing cursor position in input
      e.preventDefault();
      onSlideLeft(e);
    } else if (e.keyCode === KEY_CODE_ARROW_RIGHT) {
      // Prevent changing cursor position in input
      e.preventDefault();
      onSlideRight(e);
    }
  };

  return (
    // A <section> (Ron, 24 sep 2026: confirmed via a second round of screenshots that Console's
    // own sections alternate white/#F5F6F7 by DOM POSITION, not per-section config - see below).
    // Every Console section renders as its own <section> tag (see
    // PageBuilder/SectionBuilder/SectionContainer/SectionContainer.js), and its background-color
    // CSS keys off nth-of-type position AMONG THOSE <section> SIBLINGS under <Main>
    // (SectionContainer.module.css's `&:nth-of-type(odd)` - odd = white, even = whatever shows
    // through, in practice #F5F6F7). An EARLIER version of this fix (24 sep 2026, since
    // corrected) switched this row to a <div>, reasoning that keeping it out of that count would
    // "protect" the Console sections after it from having their alternation disturbed - that
    // reasoning had it backwards: those sections were themselves plain, position-based
    // `nth-of-type(odd)` sections with no per-section override, and the whole page (this row
    // included) had already settled into a working rhythm around this row occupying a genuine
    // slot in that sequence. Switching to <div> removed that slot, which shifted every Console
    // section after this row by one position and broke THEIR alternation instead (confirmed live:
    // Bijzonder duurzaam turned the same grey as this row, when it needed to be white). Back to
    // <section> restores the original, correct count. This row's own background (set explicitly
    // below, in its own CSS module - the Console `nth-of-type` rule above doesn't apply to it,
    // different class entirely) still doesn't depend on where it lands in that count either way.
    <section className={css.root}>
      {/* H1, not H2 (Ron, 24 sep 2026: "'Nieuw op Bookya' ook in H1 in zelfde lettertype als de
          andere H1's") - see the import above for which H1 this is and why. */}
      <header className={css.sectionDetails}>
        <H1 className={css.title}>
          <FormattedMessage id="NewestListingsSection.title" />
        </H1>
        {/* New (24 sep 2026, Ron: 'onder de H1 de volgende subtekst plaatsen "Net toegevoegd:
            unieke verblijven om bij weg te dromen."') */}
        <Ingress className={css.subtitle}>
          <FormattedMessage id="NewestListingsSection.subtitle" />
        </Ingress>
      </header>
      <div className={css.carouselContainer} id={SLIDER_CONTAINER_ID}>
        <div
          className={classNames(css.carouselArrows, {
            [css.notEnoughBlocks]: listings.length <= NUM_COLUMNS,
          })}
        >
          <button className={css.carouselArrowPrev} onClick={onSlideLeft} onKeyDown={onKeyDown}>
            ‹
          </button>
          <button className={css.carouselArrowNext} onClick={onSlideRight} onKeyDown={onKeyDown}>
            ›
          </button>
        </div>
        <div className={css.listingCards} id={SLIDER_ID}>
          {listings.map(l => (
            <div className={css.listingCard} key={l.id.uuid}>
              {/* disablePhotoSwipe: this row (.listingCards below) is itself horizontally
                  swipeable to move between cards - without this, touching a card's own photo
                  hands the horizontal drag to the card's internal photo-swipe instead (via
                  touch-action: pan-y, see ListingCard.module.css), so swiping only worked when
                  starting below the photo. Ron, 4 okt 2026: "Dit werkt alleen als je onder de
                  foto swiped." Tapping a card's photo arrows/dots still browses its own photos. */}
              <ListingCard listing={l} renderSizes={renderSizes} disablePhotoSwipe />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const mapStateToProps = state => {
  return {
    listings: getNewestListings(state),
  };
};

const NewestListingsSection = compose(connect(mapStateToProps))(NewestListingsSectionComponent);

export default NewestListingsSection;
